/**
 * Which tool a page gets, and how it is started.
 *
 * Separate from the entry points, which are the bundles' side effects and do
 * nothing but call this on the document. Keeping the side effect in modules of
 * their own is what lets this one be imported and exercised without a page.
 *
 * The starters are passed in rather than reached for, because there is a
 * bundle per widget now rather than one for all of them: `main-hanzi.ts` knows
 * about the dictionary and `main-numbers.ts` does not, which is the whole
 * point of them being separate files. A list kept here would put every tool
 * back into every bundle.
 */

/** What starts one tool on the container the template marked for it. */
export type ToolStarter = (container: HTMLElement) => void;

/**
 * Start whichever of these tools the page asked for.
 *
 * A page carrying no `data-cb-tool` at all starts nothing, and so does one
 * naming a tool this bundle does not have. Both are worth allowing rather than
 * reporting: the second is what a page gets if the layout ever links the wrong
 * bundle, and a dead widget is a better failure than a thrown error on top of
 * one.
 */
export function startTools(
  root: ParentNode,
  starters: ReadonlyMap<string, ToolStarter>,
): void {
  for (const container of root.querySelectorAll<HTMLElement>(
    "[data-cb-tool]",
  )) {
    const name = container.dataset["cbTool"];

    if (name !== undefined) {
      starters.get(name)?.(container);
    }
  }
}
